import { lazy, Suspense } from 'react'
import { createBrowserRouter, createRoutesFromElements, redirect, Route } from 'react-router-dom'

import { PageLoader } from '@/components/PageLoader'
import { DOC_TYPE_CONFIG } from '@/pages/docs/constants'

import packageJson from '../../package.json'
import { AuthorizedOutlet } from '../auth/authorized-outlet'
import { PageGuard } from '../auth/page-guard'
import { SignIn } from '../auth/sign-in'
import { Debug } from '../components/debug'
import { MainLayout } from '../layout/MainLayout/MainLayout'
import { EmmaPath } from './paths'

// Lazy load page components
const AutoBookingPage = lazy(() => import('@/pages/autoBooking'))
const BranchDetailsPage = lazy(() => import('@/pages/branch/Details'))
const BranchPage = lazy(() => import('@/pages/branch'))
const CalendarAvailabilities = lazy(() => import('@/pages/calendarAvailabilities'))
const CronJobsPage = lazy(() => import('@/pages/cronJobs'))
const CustomerPage = lazy(() => import('@/pages/customer'))
const CustomerSearch = lazy(() => import('@/pages/customerSearch'))
const DialpadStatsPage = lazy(() => import('@/pages/report/dialpadStats'))
const EmployeeDetailsPage = lazy(() => import('@/pages/employee/Details'))
const EmployeePage = lazy(() => import('@/pages/employee'))
const EnhancedSalesPage = lazy(() => import('@/pages/enhancedSales'))
const Leads = lazy(() => import('@/pages/leads'))
const LeadsTriage = lazy(() => import('@/pages/leadsTriage'))
const LeadsTriageV2 = lazy(() => import('@/pages/leadsTriageV2'))
const LeadsTriageV4 = lazy(() => import('@/pages/leadsTriageV4'))
const MoveManager = lazy(() => import('@/pages/moveManager'))
const MoveReports = lazy(() => import('@/pages/moveReports'))
const MoverMovesPage = lazy(() => import('@/pages/moverMoves'))
const MySchedulePage = lazy(() => import('@/pages/mySchedule'))
const NPSPage = lazy(() => import('@/pages/nps'))
const NotificationRulesPage = lazy(() => import('@/pages/notificationRules'))
const TrackingIncorrectMovesPage = lazy(() => import('@/pages/trackingIncorrectMoves'))
const PricingRulesPage = lazy(() => import('@/pages/pricingRules'))
const ProblemLocation = lazy(() => import('@/pages/problemLocation'))
const ReviewsPage = lazy(() => import('@/pages/report/reviews'))
const RipplingWorkHoursPage = lazy(() => import('@/pages/ripplingWorkHours'))
const ServiceZonesPage = lazy(() => import('@/pages/serviceZones'))
const TripTrackerPage = lazy(() => import('@/pages/tripTracker'))
const TruckDetailsPage = lazy(() => import('@/pages/truck/Details'))
const TruckPage = lazy(() => import('@/pages/truck'))
const ZipZonePage = lazy(() => import('@/pages/zipZone'))
const MovesMapPage = lazy(() => import('@/pages/report/movesMap'))
const BookingSimulatorPage = lazy(() => import('@/pages/bookingSimulator'))
const EstimationSimulatorPage = lazy(() => import('@/pages/estimationSimulator'))
const ReassignmentFormPage = lazy(() => import('@/pages/reassignmentForm'))
const DocsPage = lazy(() => import('@/pages/docs'))
const EmailTemplatesPage = lazy(() => import('@/pages/emailTemplates'))
const SalesReviewPage = lazy(() => import('@/pages/salesReview'))
const SalesReviewHistoryPage = lazy(() => import('@/pages/salesReviewHistory'))
const EmmaLeadsPage = lazy(() => import('@/pages/emmaLeads'))
const EmailTemplateDetailsPage = lazy(() => import('@/pages/emailTemplates/Details'))
const LeadfernoConversionPage = lazy(() => import('@/pages/leadfernoConversion'))

// Report pages
const BillableEfficiencyPage = lazy(() => import('@/pages/report/billableEfficiency'))
const ContractEfficiencyPage = lazy(() => import('@/pages/report/contractEfficiency'))
const GeneralReportPage = lazy(() => import('@/pages/report/general'))
const KPIsReportPage = lazy(() => import('@/pages/report/kpis'))
const MapPage = lazy(() => import('@/pages/report/map'))
const DamagesPage = lazy(() => import('@/pages/report/damage'))
const AttendancePointsPage = lazy(() => import('@/pages/report/attendancePoints'))
const EinsteinGamesReportPage = lazy(() => import('@/pages/report/einsteinGames'))
const EinsteinGamesRulesPage = lazy(() => import('@/pages/report/einsteinGames/rules'))
const MoverGamesDetailsPage = lazy(() => import('@/pages/report/einsteinGames/MoverDetails'))
const NPSReportPage = lazy(() => import('@/pages/report/nps'))
const OverTimePage = lazy(() => import('@/pages/report/overTime'))
const RevenueDashboardPage = lazy(() => import('@/pages/report/revenue'))
const RevenueReportDetailsPage = lazy(() => import('@/pages/report/revenue/details'))
const SalesPipelinePage = lazy(() => import('@/pages/report/salesPipeline'))
const SalesReportDetailsPage = lazy(() => import('@/pages/report/sales/details'))
const SalesReportPage = lazy(() => import('@/pages/report/sales'))
const SettingsReportPage = lazy(() => import('@/pages/report/settings'))
const TurReportPage = lazy(() => import('@/pages/report/tur'))
const BranchKPIsReportPage = lazy(() => import('@/pages/report/branchKPIs'))
const RelocationTrendMapPage = lazy(() => import('@/pages/report/relocationMap'))
const DriverScoreReportPage = lazy(() => import('@/pages/report/driverScore'))
const PraiseReportPage = lazy(() => import('@/pages/report/praise'))
const PerformanceLogPage = lazy(() => import('@/pages/performanceLog'))
const AttendanceApprovalsPage = lazy(() => import('@/pages/attendanceApprovals'))
const EcpFeedbackPage = lazy(() => import('@/pages/ecpFeedback'))
const MarketingDashboardPage = lazy(() => import('@/pages/report/marketingDashboard'))
const SnapshotPage = lazy(() => import('@/pages/report/snapshot'))

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
)

export const Router = () => {
  return createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path={EmmaPath.SIGN_IN} element={<SignIn />} />
        <Route path={EmmaPath.EMMA} element={<AuthorizedOutlet authorizedRoles={[]} />}>
          <Route path={EmmaPath.EMMA} element={<MainLayout />}>
            <Route index loader={() => redirect(EmmaPath.ACCOUNT_MY_SCHEDULE)} />
            <Route
              path={EmmaPath.CUSTOMER_LISTING}
              element={
                <PageGuard path={EmmaPath.CUSTOMER_LISTING}>
                  <SuspenseWrapper>
                    <CustomerSearch />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.CUSTOMER_CREATE}
              element={
                <SuspenseWrapper>
                  <CustomerPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.CUSTOMER_PAGE}
              element={
                <SuspenseWrapper>
                  <CustomerPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.CUSTOMER_NEW_LEADS}
              element={
                <PageGuard path={EmmaPath.CUSTOMER_NEW_LEADS}>
                  <SuspenseWrapper>
                    <Leads />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE}
              element={
                <PageGuard path={EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE}>
                  <SuspenseWrapper>
                    <LeadsTriage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE_V2}
              element={
                <PageGuard path={EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE_V2}>
                  <SuspenseWrapper>
                    <LeadsTriageV2 />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE_V4}
              element={
                <PageGuard path={EmmaPath.CUSTOMER_NEW_LEADS_TRIAGE_V4}>
                  <SuspenseWrapper>
                    <LeadsTriageV4 />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.CUSTOMER_AUTO_BOOKINGS}
              element={
                <PageGuard path={EmmaPath.CUSTOMER_AUTO_BOOKINGS}>
                  <SuspenseWrapper>
                    <AutoBookingPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.CUSTOMER_ENHANCED_SALES}
              element={
                <SuspenseWrapper>
                  <EnhancedSalesPage />
                </SuspenseWrapper>
              }
            />

            <Route
              path={EmmaPath.MOVE_MANAGER}
              element={
                <PageGuard path={EmmaPath.MOVE_MANAGER}>
                  <SuspenseWrapper>
                    <MoveManager />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.MOVE_MANAGER_DAY}
              element={
                <PageGuard path={EmmaPath.MOVE_MANAGER}>
                  <SuspenseWrapper>
                    <MoveManager />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />

            <Route
              path={EmmaPath.ADMIN_BRANCH_LISTING}
              element={
                <PageGuard path={EmmaPath.ADMIN_BRANCH_LISTING}>
                  <SuspenseWrapper>
                    <BranchPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_BRANCH_CREATE}
              element={
                <PageGuard path={EmmaPath.ADMIN_BRANCH_LISTING}>
                  <SuspenseWrapper>
                    <BranchDetailsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_BRANCH_UPDATE}
              element={
                <PageGuard path={EmmaPath.ADMIN_BRANCH_LISTING}>
                  <SuspenseWrapper>
                    <BranchDetailsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />

            <Route
              path={EmmaPath.ADMIN_EMAIL_TEMPLATES}
              element={
                <PageGuard path={EmmaPath.ADMIN_EMAIL_TEMPLATES}>
                  <SuspenseWrapper>
                    <EmailTemplatesPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_EMAIL_TEMPLATES_CREATE}
              element={
                <PageGuard path={EmmaPath.ADMIN_EMAIL_TEMPLATES}>
                  <SuspenseWrapper>
                    <EmailTemplateDetailsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_EMAIL_TEMPLATES_UPDATE}
              element={
                <PageGuard path={EmmaPath.ADMIN_EMAIL_TEMPLATES}>
                  <SuspenseWrapper>
                    <EmailTemplateDetailsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />

            <Route
              path={EmmaPath.ADMIN_TRUCK_LISTING}
              element={
                <PageGuard path={EmmaPath.ADMIN_TRUCK_LISTING}>
                  <SuspenseWrapper>
                    <TruckPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_TRUCK_CREATE}
              element={
                <PageGuard path={EmmaPath.ADMIN_TRUCK_LISTING}>
                  <SuspenseWrapper>
                    <TruckDetailsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_TRUCK_UPDATE}
              element={
                <PageGuard path={EmmaPath.ADMIN_TRUCK_LISTING}>
                  <SuspenseWrapper>
                    <TruckDetailsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />

            <Route
              path={EmmaPath.ADMIN_EMPLOYEE_LISTING}
              element={
                <PageGuard path={EmmaPath.ADMIN_EMPLOYEE_LISTING}>
                  <SuspenseWrapper>
                    <EmployeePage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_EMPLOYEE_CREATE}
              element={
                <PageGuard path={EmmaPath.ADMIN_EMPLOYEE_LISTING}>
                  <SuspenseWrapper>
                    <EmployeeDetailsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_EMPLOYEE_UPDATE}
              element={
                <PageGuard path={EmmaPath.ADMIN_EMPLOYEE_LISTING}>
                  <SuspenseWrapper>
                    <EmployeeDetailsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />

            <Route
              path={EmmaPath.ADMIN_TRIP_TRACKER_LISTING}
              element={
                <SuspenseWrapper>
                  <TripTrackerPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.ADMIN_NPS_LISTING}
              element={
                <SuspenseWrapper>
                  <NPSPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.ADMIN_RIPPLING_WORK_HOURS_LISTING}
              element={
                <SuspenseWrapper>
                  <RipplingWorkHoursPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_DIALPAD_STATS}
              element={
                <SuspenseWrapper>
                  <DialpadStatsPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_REVIEWS}
              element={
                <SuspenseWrapper>
                  <ReviewsPage />
                </SuspenseWrapper>
              }
            />

            <Route
              path={EmmaPath.REPORT_DAMAGES}
              element={
                <SuspenseWrapper>
                  <DamagesPage />
                </SuspenseWrapper>
              }
            />

            {/* <Route path={EmmaPath.ADMIN_MARKETING} element={<MarketingSourceEditor />} /> */}

            <Route
              path={EmmaPath.ADMIN_PROBLEMS_LOCATIONS}
              element={
                <PageGuard path={EmmaPath.ADMIN_PROBLEMS_LOCATIONS}>
                  <SuspenseWrapper>
                    <ProblemLocation />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_MOVE_REPORTS}
              element={
                <PageGuard path={EmmaPath.ADMIN_MOVE_REPORTS}>
                  <SuspenseWrapper>
                    <MoveReports />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_CALENDAR_AVAILABILITIES}
              element={
                <PageGuard path={EmmaPath.ADMIN_CALENDAR_AVAILABILITIES}>
                  <SuspenseWrapper>
                    <CalendarAvailabilities />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_PRICING_RULES}
              element={
                <PageGuard path={EmmaPath.ADMIN_PRICING_RULES}>
                  <SuspenseWrapper>
                    <PricingRulesPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_NOTIFICATION_RULES}
              element={
                <PageGuard path={EmmaPath.ADMIN_NOTIFICATION_RULES}>
                  <SuspenseWrapper>
                    <NotificationRulesPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_TRACKING_INCORRECT_MOVES}
              element={
                <PageGuard path={EmmaPath.ADMIN_TRACKING_INCORRECT_MOVES}>
                  <SuspenseWrapper>
                    <TrackingIncorrectMovesPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_ZIP_ZONES}
              element={
                <PageGuard path={EmmaPath.ADMIN_ZIP_ZONES}>
                  <SuspenseWrapper>
                    <ZipZonePage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_SERVICE_ZONES}
              element={
                <PageGuard path={EmmaPath.ADMIN_SERVICE_ZONES}>
                  <SuspenseWrapper>
                    <ServiceZonesPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_MAP}
              element={
                <PageGuard path={EmmaPath.ADMIN_MAP}>
                  <SuspenseWrapper>
                    <MapPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_CRON_JOBS}
              element={
                <PageGuard path={EmmaPath.ADMIN_CRON_JOBS}>
                  <SuspenseWrapper>
                    <CronJobsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_RELOCATION_TREND_MAP}
              element={
                <PageGuard path={EmmaPath.ADMIN_RELOCATION_TREND_MAP}>
                  <SuspenseWrapper>
                    <RelocationTrendMapPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            {/* Unified Docs Route */}
            <Route
              path={EmmaPath.ADMIN_DOCS}
              element={
                <PageGuard path={EmmaPath.ADMIN_DOCS}>
                  <SuspenseWrapper>
                    <DocsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            {/* Redirect docs without page to default page */}
            <Route
              path="/emma/admin/docs/documentation"
              loader={() => redirect(`/emma/admin/docs/documentation/${DOC_TYPE_CONFIG.documentation.defaultPage}`)}
            />
            <Route
              path="/emma/admin/docs/handbook"
              loader={() => redirect(`/emma/admin/docs/handbook/${DOC_TYPE_CONFIG.handbook.defaultPage}`)}
            />

            <Route
              path={EmmaPath.REPORT_KPIS}
              element={
                <SuspenseWrapper>
                  <KPIsReportPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_BRANCH_KPIS}
              element={
                <PageGuard path={EmmaPath.REPORT_BRANCH_KPIS}>
                  <SuspenseWrapper>
                    <BranchKPIsReportPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.REPORT_NPS}
              element={
                <SuspenseWrapper>
                  <NPSReportPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_GENERAL}
              element={
                <SuspenseWrapper>
                  <GeneralReportPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_TUR}
              element={
                <SuspenseWrapper>
                  <TurReportPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_SALES}
              element={
                <PageGuard path={EmmaPath.REPORT_SALES}>
                  <SuspenseWrapper>
                    <SalesReportPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.REPORT_SALES_PIPELINE}
              element={
                <SuspenseWrapper>
                  <SalesPipelinePage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_REVENUE}
              element={
                <SuspenseWrapper>
                  <RevenueDashboardPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_ATTENDANCE_POINTS}
              element={
                <SuspenseWrapper>
                  <AttendancePointsPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_OVER_TIME}
              element={
                <PageGuard path={EmmaPath.REPORT_OVER_TIME}>
                  <SuspenseWrapper>
                    <OverTimePage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.REPORT_SALES_DETAILS}
              element={
                <SuspenseWrapper>
                  <SalesReportDetailsPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_REVENUE_DETAILS}
              element={
                <SuspenseWrapper>
                  <RevenueReportDetailsPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_SETTINGS}
              element={
                <PageGuard path={EmmaPath.REPORT_SETTINGS}>
                  <SuspenseWrapper>
                    <SettingsReportPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.REPORT_BILLABLE_EFFICIENCY}
              element={
                <SuspenseWrapper>
                  <BillableEfficiencyPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_CONTRACT_EFFICIENCY}
              element={
                <PageGuard path={EmmaPath.REPORT_CONTRACT_EFFICIENCY}>
                  <SuspenseWrapper>
                    <ContractEfficiencyPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.REPORT_EINSTEIN_GAMES}
              element={
                <PageGuard path={EmmaPath.REPORT_EINSTEIN_GAMES}>
                  <SuspenseWrapper>
                    <EinsteinGamesReportPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.REPORT_EINSTEIN_GAMES_RULES}
              element={
                <PageGuard path={EmmaPath.REPORT_EINSTEIN_GAMES}>
                  <SuspenseWrapper>
                    <EinsteinGamesRulesPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.REPORT_MOVER_GAMES}
              element={
                <SuspenseWrapper>
                  <MoverGamesDetailsPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_DRIVER_SCORE}
              element={
                <SuspenseWrapper>
                  <DriverScoreReportPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_PRAISE}
              element={
                <SuspenseWrapper>
                  <PraiseReportPage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.REPORT_SNAPSHOT}
              element={
                <PageGuard path={EmmaPath.REPORT_SNAPSHOT}>
                  <SuspenseWrapper>
                    <SnapshotPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_MOVES_MAP}
              element={
                <PageGuard path={EmmaPath.ADMIN_MOVES_MAP}>
                  <SuspenseWrapper>
                    <MovesMapPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_BOOKING_SIMULATOR}
              element={
                <PageGuard path={EmmaPath.ADMIN_BOOKING_SIMULATOR}>
                  <SuspenseWrapper>
                    <BookingSimulatorPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_LEADFERNO_CONVERSION}
              element={
                <PageGuard path={EmmaPath.ADMIN_LEADFERNO_CONVERSION}>
                  <SuspenseWrapper>
                    <LeadfernoConversionPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_PERFORMANCE_LOG}
              element={
                <PageGuard path={EmmaPath.ADMIN_PERFORMANCE_LOG}>
                  <SuspenseWrapper>
                    <PerformanceLogPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_ATTENDANCE_APPROVALS}
              element={
                <PageGuard path={EmmaPath.ADMIN_ATTENDANCE_APPROVALS}>
                  <SuspenseWrapper>
                    <AttendanceApprovalsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ECP_QUICK_PRAISE_REDIRECTS}
              element={
                <PageGuard path={EmmaPath.ECP_QUICK_PRAISE_REDIRECTS}>
                  <SuspenseWrapper>
                    <EcpFeedbackPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_ESTIMATION_SIMULATOR}
              element={
                <PageGuard path={EmmaPath.ADMIN_ESTIMATION_SIMULATOR}>
                  <SuspenseWrapper>
                    <EstimationSimulatorPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_PERFORMANCE_LOG}
              element={
                <PageGuard path={EmmaPath.ADMIN_PERFORMANCE_LOG}>
                  <SuspenseWrapper>
                    <PerformanceLogPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.ADMIN_REASSIGNMENT_FORM}
              element={
                <PageGuard path={EmmaPath.ADMIN_REASSIGNMENT_FORM}>
                  <SuspenseWrapper>
                    <ReassignmentFormPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />

            <Route
              path={EmmaPath.ACCOUNT_MY_SCHEDULE}
              element={
                <SuspenseWrapper>
                  <MySchedulePage />
                </SuspenseWrapper>
              }
            />
            <Route
              path={EmmaPath.MOVER_MOVES}
              element={
                <PageGuard path={EmmaPath.MOVER_MOVES}>
                  <SuspenseWrapper>
                    <MoverMovesPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.SALES_MANAGER_REVIEW}
              element={
                <PageGuard path={EmmaPath.SALES_MANAGER_REVIEW}>
                  <SuspenseWrapper>
                    <SalesReviewPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.SALES_MANAGER_REVIEW_HISTORY}
              element={
                <PageGuard path={EmmaPath.SALES_MANAGER_REVIEW}>
                  <SuspenseWrapper>
                    <SalesReviewHistoryPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.EMMA_LEADS}
              element={
                <PageGuard path={EmmaPath.EMMA_LEADS}>
                  <SuspenseWrapper>
                    <EmmaLeadsPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
            <Route
              path={EmmaPath.REPORT_MARKETING_DASHBOARD}
              element={
                <PageGuard path={EmmaPath.REPORT_MARKETING_DASHBOARD}>
                  <SuspenseWrapper>
                    <MarketingDashboardPage />
                  </SuspenseWrapper>
                </PageGuard>
              }
            />
          </Route>
        </Route>
        <Route
          path="/version"
          element={
            <pre style={{ padding: 20 }}>
              {JSON.stringify({ version: packageJson.version, name: packageJson.name }, null, 2)}
            </pre>
          }
        />
        <Route path={EmmaPath.ROOT} loader={() => redirect(EmmaPath.EMMA)} />
        <Route path={EmmaPath.UNHANDLED} element={<Debug />} />
        <Route path={EmmaPath.DEBUG} element={<Debug />} />
        {/* <Route path='*' element={<Navigate to={EmmaPath.SIGN_IN} />} /> */}
      </>,
    ),
  )
}
